import { supabase } from '../services/supabaseService';
import {
    isWebsiteChannelApplication,
    isWebsiteTalentPoolSourceStatus,
    websiteChannelSourceDisplayLabel,
} from './candidateJourneyBuckets';

export type CandidateClientProfileRow = {
    id?: string;
    candidate_id: string;
    client_id?: string | null;
    status?: string | null;
    source?: string | null;
    notes?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
    candidates?: { id?: string; name?: string | null; email?: string | null } | null;
};

/** PostgREST: legacy table removed — talent pool data lives on `candidates`. */
function isLegacyProfilesTableMissing(error: { code?: string; message?: string } | null | undefined): boolean {
    if (!error) return false;
    const code = String(error.code || '');
    const msg = String(error.message || '').toLowerCase();
    return (
        code === 'PGRST205' ||
        code === '42P01' ||
        msg.includes('candidate_client_profiles') ||
        msg.includes('schema cache')
    );
}

const CANDIDATE_PROFILE_SELECT = 'id, client_id, status, source, name, email, added_at, updated_at';

function candidateToProfileRow(row: Record<string, unknown>): CandidateClientProfileRow {
    const id = String(row.id || '').trim();
    return {
        id,
        candidate_id: id,
        client_id: row.client_id != null ? String(row.client_id) : null,
        status: row.status != null ? String(row.status) : null,
        source: row.source != null ? String(row.source) : null,
        notes: null,
        created_at: (row.added_at ?? row.created_at ?? null) as string | null,
        updated_at: (row.updated_at ?? null) as string | null,
        candidates: {
            id,
            name: row.name != null ? String(row.name) : null,
            email: row.email != null ? String(row.email) : null,
        },
    };
}

/** Known talent-pool bronstatussen (exact + common variants). */
export const WEBSITE_TALENT_POOL_STATUS_VALUES = [
    'werkenbijvideoworks',
    'wp-interim',
    'wpinterim',
    'website sollicitatie',
    'Website Sollicitatie',
    'webreactie',
    'Website (wp-interim.nl)',
];

function profileNotesIndicateWebsite(notes: unknown): boolean {
    const t = String(notes || '').toLowerCase();
    return t.includes('externe website webhook') || t.includes('ingekomen via externe website');
}

export function profileIndicatesWebsiteChannel(p: CandidateClientProfileRow): boolean {
    if (isWebsiteTalentPoolSourceStatus(p?.status)) return true;
    if (isWebsiteTalentPoolSourceStatus(p?.source)) return true;
    return profileNotesIndicateWebsite(p?.notes);
}

/** Website-bron kandidaten zonder sollicitatie in de periode (voorheen candidate_client_profiles). */
export async function fetchWebsiteSourceProfiles(): Promise<CandidateClientProfileRow[]> {
    const byId = new Map<string, CandidateClientProfileRow>();
    const statuses = [...new Set(WEBSITE_TALENT_POOL_STATUS_VALUES.map((s) => s.trim()).filter(Boolean))];

    const ingest = (rows: Record<string, unknown>[] | null | undefined) => {
        for (const row of rows || []) {
            const pr = candidateToProfileRow(row);
            if (!profileIndicatesWebsiteChannel(pr)) continue;
            const key = pr.candidate_id;
            if (key) byId.set(key, pr);
        }
    };

    try {
        for (let i = 0; i < statuses.length; i += 12) {
            const chunk = statuses.slice(i, i + 12);
            let from = 0;
            while (from < 20000) {
                const { data, error } = await supabase
                    .from('candidates')
                    .select(CANDIDATE_PROFILE_SELECT)
                    .in('status', chunk)
                    .order('updated_at', { ascending: false })
                    .range(from, from + 999);
                if (error) {
                    if (isLegacyProfilesTableMissing(error)) return [];
                    console.warn('[fetchWebsiteSourceProfiles] status chunk', error.message);
                    break;
                }
                if (!data?.length) break;
                ingest(data as Record<string, unknown>[]);
                if (data.length < 1000) break;
                from += 1000;
            }
        }

        const orFilters = [
            'status.ilike.%werkenbijvideoworks%',
            'status.ilike.%wp-interim%',
            'status.ilike.%wpinterim%',
            'status.ilike.%website%sollicitatie%',
            'status.ilike.%webreactie%',
            'source.ilike.%werkenbijvideoworks%',
            'source.ilike.%wp-interim%',
            'source.ilike.%wpinterim%',
            'source.ilike.%website%sollicitatie%',
            'source.ilike.%webreactie%',
        ];
        let from = 0;
        while (from < 15000) {
            const { data, error } = await supabase
                .from('candidates')
                .select(CANDIDATE_PROFILE_SELECT)
                .or(orFilters.join(','))
                .order('updated_at', { ascending: false })
                .range(from, from + 999);
            if (error) {
                if (isLegacyProfilesTableMissing(error)) return Array.from(byId.values());
                console.warn('[fetchWebsiteSourceProfiles] ilike scan', error.message);
                break;
            }
            if (!data?.length) break;
            ingest(data as Record<string, unknown>[]);
            if (data.length < 1000) break;
            from += 1000;
        }
    } catch (e) {
        console.warn('[fetchWebsiteSourceProfiles]', e);
    }

    return Array.from(byId.values());
}

/** Per-kandidaat profielrijen voor reporting (voorheen candidate_client_profiles). */
export async function fetchCandidateProfilesByCandidateIds(
    candidateIds: string[],
): Promise<Map<string, CandidateClientProfileRow[]>> {
    const map = new Map<string, CandidateClientProfileRow[]>();
    const ids = [...new Set(candidateIds.map((id) => String(id || '').trim()).filter(Boolean))];
    if (!ids.length) return map;

    const CHUNK_SIZE = 40;

    for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
        const chunk = ids.slice(i, i + CHUNK_SIZE);
        const { data, error } = await supabase.from('candidates').select(CANDIDATE_PROFILE_SELECT).in('id', chunk);
        if (error) {
            if (isLegacyProfilesTableMissing(error)) return map;
            console.warn('[fetchCandidateProfilesByCandidateIds]', error.message);
            continue;
        }
        for (const row of (data || []) as Record<string, unknown>[]) {
            const pr = candidateToProfileRow(row);
            const cid = pr.candidate_id;
            if (!cid) continue;
            if (!map.has(cid)) map.set(cid, []);
            map.get(cid)!.push(pr);
        }
    }
    return map;
}

function mergeProfiles(existing: CandidateClientProfileRow[] | undefined, extra: CandidateClientProfileRow[]) {
    const out = new Map<string, CandidateClientProfileRow>();
    for (const p of [...(existing || []), ...extra]) {
        const key = p.id ? String(p.id) : `${p.candidate_id}:${p.client_id || ''}`;
        out.set(key, p);
    }
    return Array.from(out.values());
}

export function enrichApplicationsWithCandidateProfiles(
    apps: any[],
    profileByCandidate: Map<string, CandidateClientProfileRow[]>,
): any[] {
    return (apps || []).map((app) => {
        const cid = String(app?.candidate_id || '').trim();
        const extra = cid ? profileByCandidate.get(cid) : undefined;
        if (!extra?.length) return app;

        const cand = app.candidates ?? app.candidate;
        if (Array.isArray(cand)) {
            const c0 = cand[0];
            if (!c0) return app;
            return {
                ...app,
                candidates: [
                    {
                        ...c0,
                        candidate_client_profiles: mergeProfiles(c0.candidate_client_profiles, extra),
                    },
                    ...cand.slice(1),
                ],
            };
        }
        if (cand && typeof cand === 'object') {
            return {
                ...app,
                candidates: {
                    ...cand,
                    candidate_client_profiles: mergeProfiles(cand.candidate_client_profiles, extra),
                },
            };
        }
        return {
            ...app,
            candidates: {
                id: cid,
                candidate_client_profiles: extra,
            },
        };
    });
}

export function countWebsiteChannelApplications(apps: any[]): number {
    return (apps || []).filter((a) => isWebsiteChannelApplication(a)).length;
}

export { isWebsiteChannelApplication, websiteChannelSourceDisplayLabel };
